import { useMutation, useQueryClient } from '@tanstack/vue-query'
import { ordersQueryKeys } from '@modules/orders/api/ordersQueryKeys'
import { cryptoWalletsKeys } from '../cryptoWalletsQueryKeys'
import { setupTrustline } from '../cryptoWalletsEndpoints'
import type { TrustlineActionInput } from '../../types/cryptoWalletTypes'

/**
 * Opens the XRPL trustline an order in `awaiting_external_opt_in` is waiting on.
 *
 * admin-service mints the investor's custody wallet on the way through when they
 * have none, so this one call is the whole action — the screen never has to
 * sequence a wallet creation before it.
 */
export function useSetupTrustlineMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (input: TrustlineActionInput) => setupTrustline(input),
    // `onSettled`, not `onSuccess`: a rejected TrustSet is recorded on the row as
    // `failed` with the ledger's reason before the error is thrown, so a failure
    // changes server state just as much as a success does. Refetching only on
    // success would leave the screen showing "not set up" for a trustline that
    // now carries a failure reason the operator needs to read.
    onSettled: (_result, _error, input) => {
      queryClient.invalidateQueries({
        queryKey: cryptoWalletsKeys.walletManagementDetail(input.investorId),
      })
      // The table's per-investor counts moved with it.
      queryClient.invalidateQueries({ queryKey: cryptoWalletsKeys.walletManagementLists() })
      queryClient.invalidateQueries({ queryKey: cryptoWalletsKeys.lists() })
      // The wallet detail's trustline-gap list is a fold over the investor's
      // orders, each carrying its own joined trustline status — so the orders
      // cache is as stale as the wallet one after this call.
      queryClient.invalidateQueries({ queryKey: ordersQueryKeys.lists() })
    },
  })
}
